import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { parivartanAPI } from '../../services/parivartanAPI';
import WorkspaceDataTable from '../../components/workspace/WorkspaceDataTable';
import WorkspaceModal from '../../components/workspace/WorkspaceModal';
import WorkspacePageHeader from '../../components/workspace/WorkspacePageHeader';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { TENDER_APP_NAME } from '../../config/branding';
import { clearWorkspaceAccess } from '../../hooks/useWorkspaceAccess';
import { rolesListPath } from './settings/settingsPaths';
import './RolesAdminPage.scss';

function formatPermissionTitle(perm) {
  const parts = String(perm).split('.');
  if (parts.length < 2) return perm;
  const action = parts[parts.length - 1].replace(/_/g, ' ');
  const scope = parts.slice(1, -1).join(' ').replace(/_/g, ' ');
  const titled = (s) => s.replace(/\b\w/g, (c) => c.toUpperCase());
  return scope ? `${titled(scope)} · ${titled(action)}` : titled(action);
}

const RolesAdminPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const listPath = rolesListPath(location.pathname);
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [detailRole, setDetailRole] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  useDocumentTitle('Roles & access');

  const load = () => {
    parivartanAPI.listRolesCatalog().then((r) => {
      setRoles(r.data?.data?.roles || []);
      setAllPermissions(r.data?.data?.allPermissions || []);
    });
  };

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const system = roles.filter((r) => r.isSystem).length;
    const custom = roles.filter((r) => !r.isSystem).length;
    return { system, custom, permissions: allPermissions.length };
  }, [roles, allPermissions]);

  const tableRows = useMemo(
    () => roles.map((r) => {
      const grants = r.grants || [];
      const workspaceAreas = ['company.', 'users.', 'roles.', 'applications.', 'audit.', 'ai.'];
      const platformCount = grants.filter((p) => workspaceAreas.some((prefix) => p.startsWith(prefix))).length;
      const tenderCount = grants.filter((p) => !workspaceAreas.some((prefix) => p.startsWith(prefix))).length;
      return {
        ...r,
        displayName: r.name || r.role,
        typeLabel: r.isSystem ? 'System' : 'Custom',
        mastersLabel: grants.some((p) => p.startsWith('masters.')) ? 'Included' : 'Hidden',
        statusLabel: r.isActive === false ? 'Inactive' : 'Active',
        platformCount,
        tenderCount,
        totalCount: grants.length
      };
    }),
    [roles]
  );

  const columns = useMemo(
    () => [
      { key: 'displayName', label: 'Role', mobileTitle: true },
      { key: 'role', label: 'Key' },
      {
        key: 'typeLabel',
        label: 'Type',
        render: (value) => (
          <Badge bg={value === 'System' ? 'secondary' : 'primary'} className="fw-normal">
            {value}
          </Badge>
        )
      },
      { key: 'platformCount', label: 'Platform' },
      { key: 'tenderCount', label: 'Tender' },
      { key: 'mastersLabel', label: 'Masters' },
      { key: 'totalCount', label: 'Total' },
      {
        key: 'statusLabel',
        label: 'Status',
        render: (value) => (
          <span className={`roles-admin__status roles-admin__status--${value === 'Active' ? 'active' : 'inactive'}`}>
            {value}
          </span>
        )
      }
    ],
    []
  );

  const openCreate = () => navigate(`${listPath}/new`);

  const openEdit = (row) => {
    if (!row?.role) return;
    navigate(`${listPath}/${encodeURIComponent(row.role)}/edit`);
  };

  const setRoleActive = async (row, isActive) => {
    setError('');
    try {
      const payload = row.isSystem
        ? { systemRole: row.role, permissions: row.permissions || [], isActive }
        : { key: row.key, name: row.name || row.role, permissions: row.permissions || [], isActive };
      await parivartanAPI.upsertRoleTemplate(payload);
      clearWorkspaceAccess();
      setMsg(isActive ? `${row.name || row.role} is active` : `${row.name || row.role} is inactive`);
      setDetailRole((current) => (current && current.role === row.role ? { ...current, isActive, statusLabel: isActive ? 'Active' : 'Inactive' } : current));
      load();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not change the role status');
    }
  };

  const removeCustom = async (row) => {
    if (!row?.key) return;
    await parivartanAPI.deleteRoleTemplate(row.key);
    setMsg('Custom role removed');
    load();
  };

  const PermissionChips = ({ list, emptyText = 'None' }) => (
    <div className="workspace-modal__perm-list">
      {list?.length
        ? list.map((p) => (
          <span key={p} className="workspace-modal__perm-chip" title={p}>
            {formatPermissionTitle(p)}
          </span>
        ))
        : <span className="text-muted small">{emptyText}</span>}
    </div>
  );

  const rowActions = (row) => [
    { type: 'view', label: 'View' },
    { type: 'edit', label: 'Edit' },
    {
      type: 'status',
      label: row.isActive === false ? 'Mark active' : 'Mark inactive',
      onClick: () => setRoleActive(row, row.isActive === false)
    }
  ];

  const detailGrants = detailRole?.grants || [];
  const workspaceAreas = ['company.', 'users.', 'roles.', 'applications.', 'audit.', 'ai.'];
  const detailPlatform = detailGrants.filter((p) => workspaceAreas.some((prefix) => p.startsWith(prefix)));
  const detailMasters = detailGrants.some((p) => p.startsWith('masters.'));
  const detailTender = detailGrants.filter((p) => p.startsWith('tenders.') || p.startsWith('personnel.') || p.startsWith('customers.') || p.startsWith('documents.') || p.startsWith('knowledge.') || p.startsWith('eligibility.') || p.startsWith('decisions.') || p.startsWith('responses.') || p.startsWith('costing.') || p.startsWith('timesheets.') || p.startsWith('reports.') || p.startsWith('securities.'));
  const detailOther = detailGrants.filter((p) => !detailPlatform.includes(p) && !detailTender.includes(p) && !p.startsWith('masters.'));

  return (
    <div className="roles-admin">
      <WorkspacePageHeader
        title="Roles & access"
        subtitle="Set the permissions on each role. Inactive roles stay on file and are hidden when a user is created or edited."
        actions={
          <Button variant="primary" size="sm" onClick={openCreate}>
            Create custom role
          </Button>
        }
      />

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {msg ? <Alert variant="success" onClose={() => setMsg('')} dismissible>{msg}</Alert> : null}

      <div className="roles-admin__stats">
        <div className="ws-stat-card">
          <div className="ws-stat-card__label">System roles</div>
          <div className="ws-stat-card__value">{stats.system}</div>
        </div>
        <div className="ws-stat-card">
          <div className="ws-stat-card__label">Custom roles</div>
          <div className="ws-stat-card__value">{stats.custom}</div>
        </div>
        <div className="ws-stat-card">
          <div className="ws-stat-card__label">Permission keys</div>
          <div className="ws-stat-card__value">{stats.permissions}</div>
        </div>
      </div>

      <WorkspaceDataTable
        title="Role directory"
        downloadPermission="roles.download"
        data={tableRows}
        columns={columns}
        searchPlaceholder="Search role name or key…"
        pageSize={10}
        onView={(row) => setDetailRole(row)}
        onEdit={openEdit}
        rowActions={rowActions}
      />

      <section className="ws-panel mt-3">
        <h2 className="ws-panel__title">How access works</h2>
        <ul className="small text-muted mb-0 ps-3">
          <li>Changing a system role applies to this company. Reset restores the built-in permissions and marks the role active.</li>
          <li>Only active roles appear when a user is created or edited.</li>
          <li>Masters is a separate permission on the role. Clear it to hide the Masters menu from that role.</li>
          <li>Users can hold <strong>multiple roles</strong>; permissions are combined.</li>
          <li><strong>Grants</strong> add permissions; <strong>denials</strong> remove them (set per user).</li>
          <li>Denials override grants and role permissions.</li>
        </ul>
      </section>

      <WorkspaceModal
        show={!!detailRole}
        onHide={() => setDetailRole(null)}
        title={detailRole?.displayName || detailRole?.role}
        description={detailRole?.isSystem ? 'Built-in role. Permission changes apply to this company only.' : 'Custom role template for your organisation.'}
        footer={<Button variant="outline-secondary" onClick={() => setDetailRole(null)}>Close</Button>}
      >
        <div className="mb-3">
          <Badge bg={detailRole?.isSystem ? 'secondary' : 'primary'}>{detailRole?.typeLabel}</Badge>
          <span className={`roles-admin__status ms-2 roles-admin__status--${detailRole?.isActive === false ? 'inactive' : 'active'}`}>
            {detailRole?.isActive === false ? 'Inactive' : 'Active'}
          </span>
          <span className="text-muted small ms-2 font-monospace">{detailRole?.role}</span>
          <span className={`roles-admin__status ms-2 roles-admin__status--${detailMasters ? 'active' : 'inactive'}`}>
            {detailMasters ? 'Masters included' : 'Masters hidden'}
          </span>
        </div>
        <div className="roles-admin__detail-grid">
          <section className="workspace-modal__section">
            <div className="workspace-modal__section-label">Platform ({detailPlatform.length})</div>
            <PermissionChips list={detailPlatform} />
          </section>
          <section className="workspace-modal__section">
            <div className="workspace-modal__section-label">{TENDER_APP_NAME} ({detailTender.length})</div>
            <PermissionChips list={detailTender} />
          </section>
        </div>
        {detailOther.length ? (
          <section className="workspace-modal__section mt-3">
            <div className="workspace-modal__section-label">Other ({detailOther.length})</div>
            <PermissionChips list={detailOther} />
          </section>
        ) : null}
        {detailRole?.editable ? (
          <div className="mt-3 d-flex flex-wrap gap-2">
            <Button size="sm" variant="outline-primary" onClick={() => { setDetailRole(null); openEdit(detailRole); }}>
              Edit permissions
            </Button>
            {detailRole.isSystem && detailRole.overridden ? (
              <Button size="sm" variant="outline-secondary" onClick={async () => {
                await parivartanAPI.resetRoleTemplate({ systemRole: detailRole.role });
                clearWorkspaceAccess();
                setMsg('Built-in permissions restored');
                setDetailRole(null);
                load();
              }}
              >
                Reset to built-in
              </Button>
            ) : null}
            {!detailRole.isSystem && detailRole.key ? (
              <Button size="sm" variant="outline-danger" onClick={() => { removeCustom(detailRole); setDetailRole(null); }}>
                Delete role
              </Button>
            ) : null}
          </div>
        ) : null}
      </WorkspaceModal>
    </div>
  );
};

export default RolesAdminPage;
